import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { FreeformTextBlock } from '@course-authoring/shared-types';
import { isRichTextContentEmpty, useTheme } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';

import {
  createRichTextRendererExtensions,
  createRichTextContentResetStyles,
  safeGenerateNonEmptyRichTextHTML,
} from './richTextRenderer';
import { usePrefersReducedMotion } from './hooks/usePrefersReducedMotion';
import { useSubmitRevealTransition } from './hooks/useSubmitRevealTransition';
import { useCommittedQuestionNarrationOutcome } from './hooks/useCommittedQuestionNarrationOutcome';
import { createSubmitRevealStageStyles } from './hooks/submitRevealStageStyles';
import { SubmitRevealButton } from './SubmitRevealButton';
import { createContentBlockStyles, interactionCard } from './styles/contentBlocks';
import { useSectionSurface } from './SectionSurfaceContext';
import { getContentBlockHeadingTypography } from './styles/titleTypography';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import { getStandardInteractionWidth } from './interactionLayout';
import type { QuestionNarrationOutcomeHandler } from './narrationEvents';
import { ResponseThresholdEmptyState } from './ResponseThresholdEmptyState';
import { RichTextContent } from './RichTextContent';

const FREEFORM_SUBMIT_TRANSITION_MS = 180;
const FREEFORM_RESULTS_ENTER_DELAY_MS = 20;
const FREEFORM_MAX_LENGTH = 500;
// Announce the live character count only once the learner is close to the
// cap — an unconditional aria-live region would announce on every keystroke.
const FREEFORM_COUNT_ANNOUNCE_THRESHOLD = FREEFORM_MAX_LENGTH - 50;
const contentResetStyles = createRichTextContentResetStyles('freeform-text-description');

/** Truncates by whole Unicode code point so a cap can never split a surrogate-pair emoji. */
function truncateByCodePoint(text: string, maxCodePoints: number): string {
  const codePoints = Array.from(text);
  if (codePoints.length <= maxCodePoints) return text;
  return codePoints.slice(0, maxCodePoints).join('');
}

export interface FreeformTextRendererProps {
  block: FreeformTextBlock;
  className?: string;
  style?: React.CSSProperties;
  onInteractionComplete?: () => void;
  /** Fires once on submit with the learner's entered text (NA-444 response logging). */
  onAnswerSubmit?: (answer: { text: string }) => void;
  onNarrationOutcomeCommitted?: QuestionNarrationOutcomeHandler;
  /**
   * Whether the player has found a prior recorded answer for this block on
   * bootstrap. `'loading'` renders a lightweight placeholder so a learner
   * can't submit a second response while the player is still checking.
   * Freeform Text never gets its text back — only this status — so there is
   * no `priorAnswerOptionId`-equivalent prop.
   */
  priorAnswerStatus?: 'loading' | 'answered' | 'unanswered';
}

export function FreeformTextRenderer({
  block,
  className,
  style,
  onInteractionComplete,
  onAnswerSubmit,
  onNarrationOutcomeCommitted,
  priorAnswerStatus = 'unanswered',
}: FreeformTextRendererProps): React.ReactElement {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const isSmallMobile = useIsMobileViewport(tokens.layout.smallMobileBreakpoint);
  const prefersReducedMotion = usePrefersReducedMotion();
  const sectionSurface = useSectionSurface();
  const styles = useMemo(() => createContentBlockStyles(tokens), [tokens]);
  const extensions = useMemo(() => createRichTextRendererExtensions(), []);
  const interactionWidth = getStandardInteractionWidth(tokens, isMobile);
  const confirmationRef = useRef<HTMLDivElement>(null);

  const alreadyAnswered = priorAnswerStatus === 'answered';
  const [text, setText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [interactionComplete, setInteractionComplete] = useState(alreadyAnswered);

  const {
    isQuestionStageVisible,
    isResultsStageVisible,
    isSubmitting,
    startTransition,
    revealImmediately,
  } = useSubmitRevealTransition({
    prefersReducedMotion,
    onInteractionComplete,
    submitTransitionMs: FREEFORM_SUBMIT_TRANSITION_MS,
    resultsEnterDelayMs: FREEFORM_RESULTS_ENTER_DELAY_MS,
    initialPhase: alreadyAnswered ? 'results' : 'question',
  });

  // The bootstrap prior-answers fetch normally resolves after this block's
  // first mount, so the lazy initial-state seeding above only covers the
  // (uncommon) case where it's already known at mount — this effect covers
  // the common case: the prop transitions from 'loading' to 'answered' after
  // mount, matching LikertScaleRenderer's identical NA-319 pattern.
  useEffect(() => {
    if (priorAnswerStatus !== 'answered' || interactionComplete) return;
    setInteractionComplete(true);
    revealImmediately();
  }, [priorAnswerStatus, interactionComplete, revealImmediately]);

  const titleLevel = block.titleLevel ?? 'h2';
  const titleText = (block.title ?? '').trim();
  const hasTitle = titleText.length > 0;
  const hasDescription = block.description != null && !isRichTextContentEmpty(block.description);

  const descriptionHtml = useMemo(() => {
    if (!hasDescription || !block.description) return null;
    return safeGenerateNonEmptyRichTextHTML(block.description, extensions);
  }, [block.description, extensions, hasDescription]);

  const titleTypography = useMemo(
    () => getContentBlockHeadingTypography(tokens, titleLevel, { isMobile, isSmallMobile }),
    [isMobile, isSmallMobile, titleLevel, tokens],
  );
  const titleStyle: React.CSSProperties = useMemo(
    () => ({
      ...styles.likertTitle,
      fontSize: titleTypography.size,
      fontWeight: titleTypography.weight,
      lineHeight: titleTypography.lineHeight,
    }),
    [styles.likertTitle, titleTypography],
  );

  const handleTextChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(truncateByCodePoint(event.target.value, FREEFORM_MAX_LENGTH));
  }, []);

  const handleSubmit = useCallback(() => {
    if (interactionComplete || text.trim().length === 0) return;
    onAnswerSubmit?.({ text });
    setInteractionComplete(true);
    startTransition();
  }, [interactionComplete, text, onAnswerSubmit, startTransition]);

  useEffect(() => {
    if (isResultsStageVisible) {
      confirmationRef.current?.focus();
    }
  }, [isResultsStageVisible]);

  const submitDisabled = text.trim().length === 0;
  const shouldShowResults = isResultsStageVisible;
  useCommittedQuestionNarrationOutcome({
    outcomeKey: shouldShowResults ? `${block.id}:submitted` : null,
    outcome: shouldShowResults ? 'submitted' : null,
    // Freeform Text always takes the hidden-results path — there is no
    // results-distribution UI for this type at all.
    presentation: shouldShowResults ? 'hidden-results' : null,
    onCommitted: onNarrationOutcomeCommitted,
  });

  const { questionStage: questionStageStyle, resultsStage: resultsStageStyle } =
    createSubmitRevealStageStyles({
      prefersReducedMotion,
      transitionPhase: isSubmitting ? 'submitting' : (isResultsStageVisible ? 'results' : 'question'),
      submitTransitionMs: FREEFORM_SUBMIT_TRANSITION_MS,
      resultsEnterMs: 220,
    });

  const characterCount = Array.from(text).length;
  const announceCount = characterCount >= FREEFORM_COUNT_ANNOUNCE_THRESHOLD;

  // Spec: label / field text 18px desktop, 16px at <=576.
  const fieldFontSize = isSmallMobile ? tokens.typography.sizes.base : tokens.typography.sizes.ui;
  const bodyFontSize = isSmallMobile ? tokens.typography.mobile.body.size : tokens.typography.body.size;

  const cardStyle: React.CSSProperties = {
    ...interactionCard(tokens),
    // Spec: cardAlt on white sections. On gray sections use the lighter `surface`
    // token so the card stays distinct from the section without going near-white.
    backgroundColor: sectionSurface === 'dark' ? tokens.backgrounds.surface : tokens.backgrounds.cardAlt,
    padding: tokens.spacing['2xl'],
    width: '100%',
    boxSizing: 'border-box',
  };

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    display: 'block',
    minHeight: '200px',
    boxSizing: 'border-box',
    padding: '16px',
    border: `1px solid ${isFocused ? tokens.ui.fixedAccent : tokens.borders.default}`,
    borderRadius: tokens.radius.xs,
    outline: 'none',
    resize: 'none',
    overflowY: 'auto',
    fontFamily: tokens.typography.fontFamily,
    fontSize: fieldFontSize,
    fontWeight: 400,
    lineHeight: 1.4,
    color: tokens.text.primary,
    backgroundColor: tokens.backgrounds.page,
    transition: 'border-color 150ms',
  };

  return (
    <div
      className={className}
      style={{
        ...styles.likertContainer,
        width: interactionWidth,
        maxWidth: '100%',
        margin: '0 auto',
        ...style,
      }}
      data-testid="freeform-text-container"
    >
      <style>{contentResetStyles}</style>

      {hasTitle || descriptionHtml ? (
        <div style={styles.likertHeader}>
          {hasTitle ? React.createElement(titleLevel, { style: titleStyle }, titleText) : null}
          {descriptionHtml ? (
            <RichTextContent
              className="freeform-text-description"
              style={
                isSmallMobile
                  ? { ...styles.likertDescription, fontSize: tokens.typography.mobile.body.size }
                  : styles.likertDescription
              }
              html={descriptionHtml}
            />
          ) : null}
        </div>
      ) : null}

      {priorAnswerStatus === 'loading' ? (
        <ResponseThresholdEmptyState variant="checking-answer" />
      ) : (
        <>
          {isQuestionStageVisible ? (
            <div style={{ ...cardStyle, ...questionStageStyle }} data-testid="freeform-text-question-stage">
              <label htmlFor={`freeform-text-${block.id}`} style={{ display: 'block', marginBottom: tokens.spacing.sm }}>
                <span style={{
                  fontFamily: tokens.typography.fontFamily,
                  fontSize: fieldFontSize,
                  fontWeight: 400,
                  lineHeight: 1.2,
                  color: tokens.text.primary,
                }}
                >
                  {t('freeform_text.response_label')}
                </span>
              </label>
              <textarea
                id={`freeform-text-${block.id}`}
                aria-label={t('freeform_text.response_label')}
                value={text}
                onChange={handleTextChange}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                disabled={interactionComplete}
                style={textareaStyle}
              />
              {/* The count sits in the 24px gap above Submit (position: absolute),
                  so it never adds height or shifts the button. */}
              <div style={{ position: 'relative', height: 0 }}>
                <span
                  aria-live={announceCount ? 'polite' : 'off'}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: tokens.spacing.xs,
                    fontFamily: tokens.typography.fontFamily,
                    fontSize: tokens.typography.sizes.base,
                    fontWeight: 400,
                    lineHeight: 1.2,
                    color: tokens.text.muted,
                  }}
                >
                  {t('freeform_text.character_count', { count: characterCount, max: FREEFORM_MAX_LENGTH })}
                </span>
              </div>

              <SubmitRevealButton
                disabled={submitDisabled}
                isSubmitting={isSubmitting}
                prefersReducedMotion={prefersReducedMotion}
                onSubmit={handleSubmit}
                baseStyle={styles.likertSubmitButton}
                disabledStyle={styles.likertSubmitButtonDisabled}
                hoverStyle={styles.actionButtonHover}
                accentColor={tokens.ui.fixedAccent}
                data-testid="freeform-text-submit-button"
              />
            </div>
          ) : null}

          {shouldShowResults ? (
            <div
              ref={confirmationRef}
              tabIndex={-1}
              style={{
                ...cardStyle,
                ...resultsStageStyle,
                outline: 'none',
                textAlign: 'center',
                fontFamily: tokens.typography.fontFamily,
                fontSize: bodyFontSize,
                fontWeight: 400,
                lineHeight: tokens.typography.body.lineHeight,
                color: tokens.text.primary,
              }}
              data-testid="freeform-text-results-stage"
            >
              <span data-testid="thank-you-message">{t('question_feedback.response_recorded')}</span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

FreeformTextRenderer.displayName = 'FreeformTextRenderer';
